import { Link, NavLink } from "react-router-dom";
import { useState } from "react";
import { Menu, X, ExternalLink, GraduationCap } from "lucide-react";
import { useSettings } from "@/lib/settings";

const DEFAULT_NAV = [
  { to: "/", label: "Home", visible: true },
  { to: "/about", label: "About", visible: true },
  { to: "/courses", label: "Courses", visible: true },
  { to: "/trainers", label: "Trainers", visible: true },
  { to: "/services", label: "Services", visible: true },
  { to: "/blog", label: "Blog", visible: true },
  { to: "/partner-with-us", label: "Partner With Us", visible: true },
  { to: "/contact", label: "Contact", visible: true },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { settings } = useSettings();
  const portal = settings?.student_portal || {};

  const menu = (settings?.nav_menu?.length ? settings.nav_menu : DEFAULT_NAV).filter((n) => n.visible !== false);
  const isExternal = (to) => /^https?:\/\//i.test(to || "");

  const linkClass = ({ isActive }) =>
    `px-4 py-2 text-sm font-medium rounded-full transition-colors ${
      isActive ? "bg-neutral-900 text-white" : "text-neutral-700 hover:bg-neutral-100"
    }`;

  const renderDesktop = (n) => {
    const tid = `nav-link-${(n.label || "").toLowerCase().replace(/\s+/g, "-")}`;
    if (isExternal(n.to)) {
      return <a key={n.to + n.label} href={n.to} target="_blank" rel="noreferrer" className="px-4 py-2 text-sm font-medium rounded-full text-neutral-700 hover:bg-neutral-100 transition-colors" data-testid={tid}>{n.label}</a>;
    }
    return <NavLink key={n.to + n.label} to={n.to} data-testid={tid} className={linkClass}>{n.label}</NavLink>;
  };

  const renderMobile = (n) => {
    const tid = `mobile-nav-${(n.label || "").toLowerCase().replace(/\s+/g, "-")}`;
    if (isExternal(n.to)) {
      return <a key={n.to + n.label} href={n.to} target="_blank" rel="noreferrer" onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-neutral-800 hover:bg-neutral-100" data-testid={tid}>{n.label}</a>;
    }
    return <NavLink key={n.to + n.label} to={n.to} onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-neutral-800 hover:bg-neutral-100" data-testid={tid}>{n.label}</NavLink>;
  };

  return (
    <header className="sticky top-0 z-40 glass-nav" data-testid="site-navbar">
      <div className="container-eq flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo-link">
          {settings?.branding?.logo_light ? (
            <img src={settings.branding.logo_light} alt="EQtech Academy" className="h-9" data-testid="nav-logo-image" />
          ) : (
            <>
              <div className="w-9 h-9 rounded-xl bg-black flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-white" />
              </div>
              <div className="leading-tight">
                <div className="font-display font-black text-base">EQtech</div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-neutral-500">Academy</div>
              </div>
            </>
          )}
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {menu.map(renderDesktop)}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <a href={portal.login || "#"} target="_blank" rel="noreferrer"
             className="text-sm font-semibold px-4 py-2 rounded-full border-2 border-neutral-900 text-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors inline-flex items-center gap-1"
             data-testid="nav-student-login">
            Student Login <ExternalLink className="w-3.5 h-3.5" />
          </a>
          <a href={portal.signup || "#"} target="_blank" rel="noreferrer" className="btn-primary-eq text-sm" data-testid="nav-enroll">
            Enroll Now
          </a>
        </div>

        <button className="lg:hidden p-2" onClick={() => setOpen(!open)} aria-label="Toggle menu" aria-expanded={open} data-testid="nav-mobile-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-neutral-100 bg-white">
          <div className="container-eq py-4 flex flex-col gap-1">
            {menu.map(renderMobile)}
            <a href={portal.login || "#"} target="_blank" rel="noreferrer"
               className="px-4 py-2.5 rounded-full border-2 border-neutral-900 text-neutral-900 text-center font-semibold mt-2 inline-flex items-center justify-center gap-1"
               data-testid="mobile-nav-student-login">
              Student Login <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <a href={portal.signup || "#"} target="_blank" rel="noreferrer" className="btn-primary-eq justify-center mt-2" data-testid="mobile-nav-enroll">
              Enroll Now
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
